import type { Account, M49Area } from "@baton/api";
import { finestArea } from "./location.ts";

/**
 * 020 FR-020-256, FR-020-260, `contracts/screens.md` § 020 Accounts and pipeline: how the clients
 * are grouped wherever they are grouped, so the Clients sidebar, the client list and the client
 * board order the same grouping the same way. The board's columns (by milestone by default, the
 * owner's decision of 2026-09-28, and by region, the owner's call of 2026-09-27), the client list's
 * grouping rows and the sidebar's country headings (2026-09-28, the owner's decision that clients are
 * grouped by country by default) are each one bucket of `groupClients`.
 *
 * Region and Sub-region are the client's M49 placement, which the account read derives from its
 * country (FR-020-255), or which is the area a client whose country is not known is located by
 * (FR-020-262, 2026-09-28); Sub-region is the intermediate region where the UN defines one under the
 * sub-region, else the sub-region, so a Nigerian client is under Western Africa and a Vietnamese one
 * under South-eastern Asia, and a client located by a region alone is in a column of its own after
 * that region's sub-regions, "Africa, region only". Country is the client's country under its name in
 * the reader's locale; a client located by a region alone has none, so the Clients sidebar and the
 * client list put it under a heading of the area it is located by, "Africa (region)", after every
 * country, and the board puts every such client in one column, Region only, last. Group its client group with the clients in none under No group, last, Milestone the key of the
 * milestone it stands in, so clients on different templates share a bucket where their milestones
 * share a key, and Type the client's type in the Team's order of types. A bucket exists only for a
 * value some client holds, but for the standing buckets a surface asks for (`standingBuckets`: the
 * board's Milestone columns, every milestone of the default template, 2026-09-28), and the clients in
 * a bucket are by name in the reader's locale.
 */
export const CLIENT_GROUPINGS = ["region", "sub_region", "country", "group", "milestone", "type"] as const;
export type ClientGrouping = (typeof CLIENT_GROUPINGS)[number];

/** What a card dropped on a column writes, or null where the grouping is not a field of the client. */
export type ColumnTarget = { readonly kind: "group"; readonly groupId: string | null } | { readonly kind: "milestone"; readonly key: string };

export interface ClientColumn {
  readonly id: string;
  readonly label: string;
  /** The ISO 3166-1 alpha-2 code of a Country bucket, whose heading carries its flag; null under every other grouping. */
  readonly country: string | null;
  /** The area of a heading of the clients located by it alone, whose heading carries the globe; null for every other bucket. */
  readonly area: M49Area | null;
  /** The icon of a Type bucket's type, which its heading carries before the name (020 FR-020-273); absent under every other grouping. */
  readonly typeIcon?: string;
  readonly clients: readonly Account[];
  readonly target: ColumnTarget | null;
}

export interface GroupingContext {
  /** An M49 area's name as the reader reads it. */
  readonly areaName: (area: M49Area) => string;
  readonly countryName: (code: string) => string;
  /** The label of the bucket of the clients in no group. */
  readonly noGroup: string;
  /** The label of the board's one Country column of the clients located by a region alone. */
  readonly regionOnly: string;
  /** The label of a region's Sub-region column of the clients located by that region alone, from the region's name. */
  readonly regionOnlyIn: (region: string) => string;
  /** The milestone keys in the order the team's default template lists them, its other keys after. */
  readonly milestoneOrder: readonly { readonly key: string; readonly name: string }[];
}

/** Whether a card may be dragged under a grouping: only where the column is a field of the client the board can write. */
export function groupingEditable(grouping: ClientGrouping): boolean {
  return grouping === "group" || grouping === "milestone";
}

/** The finest M49 area under the region: the intermediate region where there is one, else the sub-region; null for a client located by a region alone. */
export function subRegionOf(account: Account): M49Area | null {
  return account.region.intermediate_region ?? account.region.sub_region;
}

/** The bucket a client falls in, without its clients. */
export interface BucketShape {
  readonly id: string;
  readonly label: string;
  readonly country: string | null;
  readonly area: M49Area | null;
  readonly typeIcon?: string;
  /** What orders the buckets, compared in turn. */
  readonly rank: readonly (number | string)[];
  readonly target: ColumnTarget | null;
}

/** Puts one client in its bucket. */
export type Bucketer = (account: Account) => BucketShape;

/**
 * Where the Country bucketer puts the clients located by a region alone: under a heading per area,
 * named by `label` ("Africa (region)"), as the Clients sidebar and the client list draw them, or all in
 * one bucket named `label` (Region only), as the board draws them. Either way after every country.
 */
export type RegionOnlyBuckets = { readonly kind: "per_area"; readonly label: (area: M49Area) => string } | { readonly kind: "one"; readonly label: string };

/** The Country bucketer: each country by its name, then the clients located by a region alone as `regionOnly` says, the areas by their headings' names. */
export function byCountry(countryName: (code: string) => string, regionOnly: RegionOnlyBuckets): Bucketer {
  return (account) => {
    if (account.country !== null) {
      const label = countryName(account.country);
      return { id: `country:${account.country}`, label, country: account.country, area: null, rank: [0, label], target: null };
    }
    if (regionOnly.kind === "one") return { id: "country:none", label: regionOnly.label, country: null, area: null, rank: [1, ""], target: null };
    const area = finestArea(account.region);
    const label = regionOnly.label(area);
    return { id: `area:${area.code}`, label, country: null, area, rank: [1, label], target: null };
  };
}

/** The Type bucketer, which alone needs the Team's type list (only the client list offers it): a type the list does not hold goes after the others, by name. */
export function byType(typeOrder: readonly string[]): Bucketer {
  return (account) => {
    const type = account.account_type;
    const index = typeOrder.indexOf(type.id);
    return { id: `type:${type.id}`, label: type.name, country: null, area: null, typeIcon: type.icon, rank: index === -1 ? [1, type.name] : [0, index], target: null };
  };
}

/**
 * The bucketer of every other grouping, from what the account read and the Team's default template
 * say. Its Country is the board's, whose clients located by a region alone share one column; the
 * Clients sidebar and the client list group by `byCountry` with a heading per area.
 */
export function bucketerOf(grouping: Exclude<ClientGrouping, "type">, context: GroupingContext): Bucketer {
  switch (grouping) {
    case "region":
      return (account) => {
        const label = context.areaName(account.region.region);
        return { id: `region:${account.region.region.code}`, label, country: null, area: null, rank: [label], target: null };
      };
    case "sub_region":
      return (account) => {
        const region = context.areaName(account.region.region);
        const area = subRegionOf(account);
        if (area === null) return { id: `sub_region:none:${account.region.region.code}`, label: context.regionOnlyIn(region), country: null, area: null, rank: [region, 1, ""], target: null };
        const label = context.areaName(area);
        return { id: `sub_region:${area.code}`, label, country: null, area: null, rank: [region, 0, label], target: null };
      };
    case "country":
      return byCountry(context.countryName, { kind: "one", label: context.regionOnly });
    case "group":
      return (account) => {
        const group = account.account_group;
        return group === null
          ? { id: "group:none", label: context.noGroup, country: null, area: null, rank: [1, ""], target: { kind: "group", groupId: null } }
          : { id: `group:${group.id}`, label: group.name, country: null, area: null, rank: [0, group.name], target: { kind: "group", groupId: group.id } };
      };
    case "milestone":
      return (account) => {
        const { key } = account.stage;
        const index = context.milestoneOrder.findIndex((milestone) => milestone.key === key);
        const label = index === -1 ? account.stage.name : (context.milestoneOrder[index]?.name ?? account.stage.name);
        return { id: `milestone:${key}`, label, country: null, area: null, rank: index === -1 ? [1, label] : [0, index], target: { kind: "milestone", key } };
      };
  }
}

/**
 * The buckets the board draws under a grouping whether or not a client holds them: under Milestone
 * every milestone of the team's default template in its order, so the board is the whole track from
 * its first milestone to Won and Lost, an empty milestone an empty column (the owner's decision of
 * 2026-09-28); none under every other grouping, whose buckets are the values the clients hold. Each
 * is the bucket `bucketerOf` puts a client of that milestone in.
 */
export function standingBuckets(grouping: Exclude<ClientGrouping, "type">, context: GroupingContext): readonly BucketShape[] {
  if (grouping !== "milestone") return [];
  return context.milestoneOrder.map((milestone, index) => ({ id: `milestone:${milestone.key}`, label: milestone.name, country: null, area: null, rank: [0, index], target: { kind: "milestone", key: milestone.key } }));
}

/** The one collation every grouped surface orders names by: the reader's locale, letters compared without case or accent, numbers by value. */
function clientCollator(locale: string): Intl.Collator {
  return new Intl.Collator(locale, { sensitivity: "base", numeric: true });
}

function compareRanks(left: readonly (number | string)[], right: readonly (number | string)[], collator: Intl.Collator): number {
  for (let index = 0; index < Math.max(left.length, right.length); index += 1) {
    const a = left[index];
    const b = right[index];
    if (a === b) continue;
    if (a === undefined) return -1;
    if (b === undefined) return 1;
    const order = typeof a === "number" && typeof b === "number" ? a - b : collator.compare(String(a), String(b));
    if (order !== 0) return order;
  }
  return 0;
}

/**
 * The buckets of the clients under a bucketer, with the `standing` ones even when empty, in the
 * grouping's order, each holding its clients by name, or in the order `accounts` came in when
 * `within` is `read`: the client list ordered by its read's own order, the last interaction (020
 * FR-020-302), whose order a sort by name would undo.
 */
export function groupClients(accounts: readonly Account[], bucketer: Bucketer, locale: string, standing: readonly BucketShape[] = [], within: "name" | "read" = "name"): readonly ClientColumn[] {
  const buckets = new Map<string, BucketShape & { readonly clients: Account[] }>(standing.map((shape) => [shape.id, { ...shape, clients: [] }]));
  for (const account of accounts) {
    const shape = bucketer(account);
    const bucket = buckets.get(shape.id) ?? { ...shape, clients: [] };
    bucket.clients.push(account);
    buckets.set(shape.id, bucket);
  }
  const collator = clientCollator(locale);
  return [...buckets.values()]
    .sort((left, right) => compareRanks(left.rank, right.rank, collator) || collator.compare(left.id, right.id))
    .map(({ id, label, country, area, typeIcon, target, clients }) => ({ id, label, country, area, ...(typeIcon === undefined ? {} : { typeIcon }), target, clients: within === "read" ? clients : [...clients].sort((a, b) => collator.compare(a.name, b.name) || collator.compare(a.id, b.id)) }));
}
